import * as React from "react";

import {
  AccessReportsWizardAccessCriteria,
  AccessReportsWizardAnalysisTypeEnum,
  AccessReportsWizardCalculationMethodEnum,
  AccessReportsWizardNetworkStandard,
  AccessReportsWizardNetworkStandardTypeEnum,
  AccessReportsWizardProduct,
} from "@app/access-reports/shared";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { cloneDeep, isEqual } from "lodash";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";

interface AccessReportsWizardEditNetworksProps {
  accessCriteria: AccessReportsWizardAccessCriteria;
  accessReportParameters: AccessReportParametersModel;
  onProductChange: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
  product: AccessReportsWizardProduct;
}

interface AccessReportsWizardEditNetworksState {
  accessCriteria: AccessReportsWizardAccessCriteria;
}

class AccessReportsWizardEditNetworks extends React.Component<AccessReportsWizardEditNetworksProps, AccessReportsWizardEditNetworksState> {
  constructor(props: AccessReportsWizardEditNetworksProps) {
    super(props);

    this.state = {
      accessCriteria: null,
    };
  }

  public componentDidMount() {
    if (!!this.props.accessCriteria) {
      this.setStandards();
    }
  }

  public componentDidUpdate(prevProps: AccessReportsWizardEditNetworksProps) {
    if (!isEqual(prevProps.accessCriteria, this.props.accessCriteria)) {
      this.setStandards();
    }
  }

  public render() {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <label className="label-s d-block mb-0">
              Product: {this.props.accessReportParameters.product.name}
            </label>
            <label className="label-s d-block mb-3">
              {this.props.accessCriteria.name}
            </label>
          </div>
        </div>

        {!!this.state.accessCriteria && this.renderForm()}

        <FlyoutFooter
          submitButton={
            <QESButton
              disabled={isEqual(this.props.accessCriteria, this.state.accessCriteria) || !this.getSpecialtyValidity()}
              qesTestId="access-reports-wizard-edit-networks_save"
              className="float-right mt-3"
              onClick={() => this.props.onProductChange(this.state.accessCriteria)}
            >
              Save
            </QESButton>
          }
        />
      </React.Fragment>
    );
  }

  private getSpecialtyValidity = (): boolean => {
    if (!this.state.accessCriteria) {
      return false;
    } else {
      for (const standard of this.state.accessCriteria.standards) {
        for (const value of Object.values(standard)) {
          if (!value || value?.toString() === "0" || value?.toString() === ".") {
            return false;
          }
        }
      }
    }

    return true;
  }

  private onInputChange = (
    standardType: AccessReportsWizardNetworkStandardTypeEnum,
    key: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const accessCriteria: AccessReportsWizardAccessCriteria = cloneDeep(this.state.accessCriteria);

    if (!!accessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = accessCriteria.standards.find(x => x.type === standardType);

      if (!!standard) {
        const updateStandard = (newValue: unknown): void => {
          if (key === "miles" || key === "minCount" || key === "minutes")
            (standard)[key] = newValue as number;
          else
            standard[key] = newValue as AccessReportsWizardNetworkStandardTypeEnum;

          this.setState({
            accessCriteria,
          });
        }

        if (!!event) {
          // on change
          const unparsedValue: string = event.target.value;
          let value: number | string = parseFloat(event.target.value);


          if (key === "miles") {
            const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

            if (re.test(unparsedValue)) {
              if (value >= 1) {
                // remove leading zeros
                value = unparsedValue.replace(/^0+/, "");
              }

              if (!isNaN(value as number) || !unparsedValue || unparsedValue === ".") {
                updateStandard((!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
              }

            }
          } else {
            if ((!isNaN(value) && unparsedValue !== "0") || !unparsedValue) {
              updateStandard(!unparsedValue ? unparsedValue : value);
            }
          }
        } else {
          // on blur
          if (!(standard)[key] ||
            ((standard)[key] as number).toString() === "0" ||
            ((standard)[key] as number).toString() === "." ||
            ((standard)[key] as number).toString() === "0."
          ) {
            // assign min value
            updateStandard(key === "miles" ? .1 : 1);
          }
        }
      }
    }
  }

  private setStandards = (): void => {
    this.setState({
      accessCriteria: cloneDeep(this.props.accessCriteria),
    });
  }

  private renderForm = (): JSX.Element => {
    let thirdLabel = "";

    switch (this.props.accessReportParameters.calculationMethod.id) {
      case AccessReportsWizardCalculationMethodEnum.Distance:
        thirdLabel = "miles.";
        break;
      case AccessReportsWizardCalculationMethodEnum.Time:
        thirdLabel = "minutes.";
        break;
      case AccessReportsWizardCalculationMethodEnum.TimeAndDistance:
        thirdLabel = "miles and";
        break;
      case AccessReportsWizardCalculationMethodEnum.TimeOrDistance:
        thirdLabel = "miles or";
        break;
      default:
        break;
    }

    const buildSection = (standardType: AccessReportsWizardNetworkStandardTypeEnum): JSX.Element => {
      const getValue = (standardType: AccessReportsWizardNetworkStandardTypeEnum, key: keyof AccessReportsWizardNetworkStandard): number => {
        const standard: AccessReportsWizardNetworkStandard = this.state.accessCriteria.standards.find(x => x.type === standardType);

        if (!!standard) {
          return (standard)[key] as number;
        }
      }

      const getStandardName = (standard: string): string => {
        const regex = /[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g;
        return standard.match(regex).join(" ");
      }

      return (
        <>
          <div className="row">
            <div className="col-12">
              <h3 className="header-m">
                {getStandardName(standardType)}
              </h3>

              <hr className="mt-0" />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-inline mb-3">
                <label className="label-s">
                  Count
                </label>
                <QESTextInput
                  className="edit-network"
                  label=""
                  testId={`access-reports-wizard__edit-networks--${standardType}-first-input`}
                  onBlur={() => this.onInputChange(standardType, "minCount")}
                  onChange={(e) => this.onInputChange(standardType, "minCount", e)}
                  value={getValue(standardType, "minCount")}
                />
                <label className="label-s">
                  in
                </label>
                <QESTextInput
                  className="edit-network"
                  label=""
                  testId={`access-reports-wizard__edit-networks--${standardType}-second-input`}
                  onBlur={() => this.onInputChange(standardType, this.props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes")}
                  onChange={(e) => this.onInputChange(standardType, this.props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes", e)}
                  value={getValue(standardType, this.props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes")}
                />
                <label className="label-s">
                  {thirdLabel}
                </label>
                {(this.props.accessReportParameters.calculationMethod.id === AccessReportsWizardCalculationMethodEnum.TimeAndDistance ||
                  this.props.accessReportParameters.calculationMethod.id === AccessReportsWizardCalculationMethodEnum.TimeOrDistance
                ) &&
                  <>
                    <QESTextInput
                      className="edit-network"
                      label=""
                      testId={`access-reports-wizard__edit-networks--${standardType}-third-input`}
                      onBlur={() => this.onInputChange(standardType, "minutes")}
                      onChange={(e) => this.onInputChange(standardType, "minutes", e)}
                      value={getValue(standardType, "minutes")}
                    />
                    <label className="label-s">
                      minutes.
                    </label>
                  </>
                }
              </div>
            </div>
          </div>
        </>
      );
    }

    switch (this.props.accessReportParameters.standard.id) {
      case AccessReportsWizardAnalysisTypeEnum.Standard:
        return buildSection(AccessReportsWizardNetworkStandardTypeEnum.Standard);
      case AccessReportsWizardAnalysisTypeEnum.DynamicByCountyClass:
        return (
          <>
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyLarge)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyMetro)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyMicro)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyRural)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC)}
          </>
        );
      case AccessReportsWizardAnalysisTypeEnum.DynamicByZipClass:
        return (
          <>
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipUrban)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipRural)}
          </>
        );
      default:
    }
  }
}

export { AccessReportsWizardEditNetworks };
